import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { alsDauer, api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import type { KursUebersicht } from './Kurse'

interface Seite {
  content: KursUebersicht[]
  totalElements: number
}

interface DateiAntwort {
  id: number
  anzahlKurse: number
  anzahlHinweise: number
}

interface FahrzeugAntwort {
  id: number
  aktiv: boolean
}

interface FahrerAntwort {
  id: number
  aktiv: boolean
  einsatzbereit: boolean
}

/**
 * Übersicht des Plattformbetreibers: Zustand der Plattform statt Fahrbetrieb.
 * Er hat selbst keine Kurse, Fahrzeuge oder Mitarbeiter.
 */
function PlattformUebersicht() {
  const [mandanten, setMandanten] = useState<
    { id: number; name: string; kuerzel: string; aktiv: boolean; anzahlBenutzer: number }[]
  >([])
  const [mail, setMail] = useState<{ versandAktiv: boolean; host: string | null } | null>(null)
  const [laedt, setLaedt] = useState(true)

  useEffect(() => {
    Promise.all([
      api.get<typeof mandanten>('/api/mandanten'),
      api.get<{ versandAktiv: boolean; host: string | null }>('/api/plattform/mail'),
    ])
      .then(([m, e]) => {
        setMandanten(m)
        setMail(e)
      })
      .finally(() => setLaedt(false))
  }, [])

  if (laedt) return <div className="laden">Wird geladen …</div>

  const aktive = mandanten.filter((m) => m.aktiv).length
  const zugaenge = mandanten.reduce((s, m) => s + m.anzahlBenutzer, 0)

  return (
    <>
      <div className="karten-raster">
        <div className="kennzahl navy">
          <div className="wert">{mandanten.length}</div>
          <div className="beschriftung">Mandanten insgesamt</div>
        </div>
        <div className="kennzahl">
          <div className="wert">{aktive}</div>
          <div className="beschriftung">davon aktiv</div>
        </div>
        <div className="kennzahl">
          <div className="wert">{zugaenge}</div>
          <div className="beschriftung">Zugänge auf der Plattform</div>
        </div>
        <div className={`kennzahl ${mail?.versandAktiv ? '' : 'orange'}`}>
          <div className="wert" style={{ fontSize: '1.2rem', paddingTop: 8 }}>
            {mail?.versandAktiv ? 'aktiv' : 'abgeschaltet'}
          </div>
          <div className="beschriftung">
            Mailversand{mail?.host ? ` · ${mail.host}` : ' · kein Server hinterlegt'}
          </div>
        </div>
      </div>

      {!mail?.versandAktiv && (
        <div className="meldung hinweis">
          Der Mailversand ist nicht aktiv. Einladungen werden erzeugt, aber nicht verschickt — der
          Link erscheint stattdessen beim Anlegen zur Weitergabe. Unter{' '}
          <Link to="/mail">Mail-Einstellungen</Link> lässt sich der SMTP-Zugang hinterlegen.
        </div>
      )}

      <div className="abschnitt">
        <h2>Mandanten</h2>
        <div className="tabelle-huelle">
          <table>
            <thead>
              <tr>
                <th>Kürzel</th>
                <th>Firma</th>
                <th>Zugänge</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {mandanten.map((m) => (
                <tr key={m.id}>
                  <td>
                    <strong>{m.kuerzel}</strong>
                  </td>
                  <td>{m.name}</td>
                  <td className="zahl">{m.anzahlBenutzer}</td>
                  <td>
                    <span className={`etikett ${m.aktiv ? 'gruen' : 'rot'}`}>
                      {m.aktiv ? 'aktiv' : 'stillgelegt'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

export function Uebersicht() {
  const { benutzer, hatRolle } = useAuth()
  const [kurse, setKurse] = useState<KursUebersicht[]>([])
  const [gesamtKurse, setGesamtKurse] = useState(0)
  const [dateien, setDateien] = useState<DateiAntwort[]>([])
  const [fahrzeuge, setFahrzeuge] = useState<FahrzeugAntwort[]>([])
  const [fahrer, setFahrer] = useState<FahrerAntwort[]>([])
  const [laedt, setLaedt] = useState(true)

  const istPlattformAdmin = hatRolle('PLATTFORM_ADMIN')

  useEffect(() => {
    if (istPlattformAdmin) {
      setLaedt(false)
      return
    }
    let abgebrochen = false
    async function laden() {
      try {
        const [k, d, fz, fa] = await Promise.all([
          api.get<Seite>('/api/kurse?groesse=200'),
          api.get<DateiAntwort[]>('/api/kursbibliothek'),
          api.get<FahrzeugAntwort[]>('/api/stammdaten/fahrzeuge'),
          api.get<FahrerAntwort[]>('/api/fahrpersonal'),
        ])
        if (abgebrochen) return
        setKurse(k.content)
        setGesamtKurse(k.totalElements)
        setDateien(d)
        setFahrzeuge(fz)
        setFahrer(fa)
      } finally {
        if (!abgebrochen) setLaedt(false)
      }
    }
    void laden()
    return () => {
      abgebrochen = true
    }
  }, [istPlattformAdmin])

  const hinweise = dateien.reduce((summe, d) => summe + d.anzahlHinweise, 0)
  const abweichungen = kurse.filter((k) => k.hatAbweichung).length
  const geteilte = kurse.filter((k) => k.geteilterDienst).length
  const wochenarbeitszeit = kurse.reduce((summe, k) => summe + (k.arbeitszeitProWoche ?? 0), 0)
  const nichtEinsatzbereit = fahrer.filter((f) => f.aktiv && !f.einsatzbereit).length

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Übersicht</h1>
          <p>
            {istPlattformAdmin
              ? `Plattformbetrieb · angemeldet als ${benutzer?.anzeigename}`
              : `${benutzer?.mandantName} · angemeldet als ${benutzer?.anzeigename}`}
          </p>
        </div>
      </div>

      {istPlattformAdmin ? (
        <PlattformUebersicht />
      ) : laedt ? (
        <div className="laden">Wird geladen …</div>
      ) : (
        <>
          <div className="karten-raster">
            <div className="kennzahl navy">
              <div className="wert">{gesamtKurse}</div>
              <div className="beschriftung">Kurse in der Planung</div>
            </div>
            <div className="kennzahl">
              <div className="wert">{dateien.length}</div>
              <div className="beschriftung">Kursblätter in der Bibliothek</div>
            </div>
            <div className="kennzahl">
              <div className="wert">{fahrzeuge.filter((f) => f.aktiv).length}</div>
              <div className="beschriftung">Fahrzeuge im Bestand</div>
            </div>
            <div className={`kennzahl ${nichtEinsatzbereit > 0 ? 'rot' : ''}`}>
              <div className="wert">{fahrer.filter((f) => f.aktiv).length}</div>
              <div className="beschriftung">
                Fahrpersonal
                {nichtEinsatzbereit > 0 && ` · ${nichtEinsatzbereit} nicht einsatzbereit`}
              </div>
            </div>
            <div className="kennzahl orange">
              <div className="wert">{geteilte}</div>
              <div className="beschriftung">Kurse mit geteiltem Dienst</div>
            </div>
            <div className={`kennzahl ${abweichungen > 0 ? 'rot' : ''}`}>
              <div className="wert">{abweichungen}</div>
              <div className="beschriftung">Kurse mit abweichender Arbeitszeit</div>
            </div>
            <div className="kennzahl navy">
              <div className="wert">{alsDauer(wochenarbeitszeit)}</div>
              <div className="beschriftung">Geplante Arbeitszeit je Woche</div>
            </div>
            <div className={`kennzahl ${hinweise > 0 ? 'orange' : ''}`}>
              <div className="wert">{hinweise}</div>
              <div className="beschriftung">Hinweise aus dem Import</div>
            </div>
          </div>

          {gesamtKurse === 0 && (
            <div className="karte">
              <h2>Erste Schritte</h2>
              <p style={{ color: 'var(--grau-700)', lineHeight: 1.6 }}>
                Laden Sie in der <Link to="/bibliothek">Kursbibliothek</Link> Ihre bestehenden
                Kursblätter als Excel-Datei hoch. VLZHUB liest Dienstzeiten, Pausen und Fahrten
                daraus ein, berechnet die Arbeitszeit selbst und meldet Abweichungen zur Vorlage.
              </p>
            </div>
          )}

          {abweichungen > 0 && (
            <div className="abschnitt">
              <h2>Kurse mit abweichender Arbeitszeit</h2>
              <div className="tabelle-huelle">
                <table>
                  <thead>
                    <tr>
                      <th>Kurs</th>
                      <th>Verkehrstage</th>
                      <th>Arbeitszeit je Woche</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {kurse
                      .filter((k) => k.hatAbweichung)
                      .map((k) => (
                        <tr key={k.id}>
                          <td>
                            <strong>{k.kursnummer}</strong>
                          </td>
                          <td>{k.verkehrstagtypText ?? k.verkehrstagtyp}</td>
                          <td className="zahl">{alsDauer(k.arbeitszeitProWoche)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <Link to={`/kurse/${k.id}`}>
                              <button className="knopf-schlicht" style={{ padding: '5px 12px' }}>
                                Ansehen
                              </button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
